/**
 * DMM B2B Registration Form
 * Multi-step React form with all 26 conditional rules from Helium
 * 
 * Steps:
 *   1. Contact Details (name, phone, email)
 *   2. Business Profile (org type, legal name, ABN, address, tax)
 *   3. Account Preferences (credit, limits, terms, PO, AP)
 *   4. Accounts Payable Contact (conditional: AP = Yes)
 *   5. Directors / Proprietors (conditional: private + credit)
 *   6. Terms & Signature (checkboxes, e-sig, uploads)
 *   7. Success
 */

(function () {
  'use strict';

  // ============================================================================
  // CONFIG
  // ============================================================================

  const WORKER_URL = 'https://b2b-registration.dishy-mini-mounts.workers.dev';
  const TURNSTILE_SITE_KEY = '0x4AAAAAAC9eJew9USRQB921'; // Cloudflare Turnstile
  const WHOLESALE_AGREEMENT_URL = 'https://cdn.shopify.com/s/files/1/0907/1581/6234/files/Wholesale_Agreement_Dishy_Mini_Mounts.pdf?v=1759295807';
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
  const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

  // Australian states
  const AU_STATES = [
    { value: '', label: 'Select a state/province...' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NSW', label: 'New South Wales' },
    { value: 'NT', label: 'Northern Territory' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'WA', label: 'Western Australia' },
  ];

  // ============================================================================
  // STATE
  // ============================================================================

  let currentStep = 1;
  let isSubmitting = false;
  let turnstileToken = null;

  const formData = {
    // Step 1: Contact
    contact: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      confirmEmail: '',
    },
    // Step 2: Business
    business: {
      orgType: '',
      legalName: '',
      tradingName: '',
      abn: '',
      acn: '',
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: 'Australia',
      zip: '',
      taxExempt: false,
      taxExemptProofUrl: null,
    },
    // Step 3: Credit/Payment
    credit: {
      requested: false,
      limit: '',
      netTerms: '',
      govtTerms: '',
      capturedNetTerms: '',
      poRequired: '',
      apRequired: false,
      signedAgreementUrl: null,
    },
    // Step 4: AP Contact
    apContact: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    },
    // Step 5: Directors
    directors: [{ fullName: '', positionTitle: '' }],
    // Step 6: Terms
    terms: {
      personalGuarantee: false,
      govtCreditAck: false,
      wholesaleTerms: false,
      signatureName: '',
      position: '',
      organisation: '',
      signatureDate: '',
      signedAgreementRequiredUrl: null,
    },
  };

  // ============================================================================
  // CONDITIONAL LOGIC (all 26 rules from Helium)
  // ============================================================================

  function isGovt() {
    return formData.business.orgType === 'government';
  }
  function isPrivate() {
    return formData.business.orgType === 'private';
  }
  function isNFP() {
    return formData.business.orgType === 'nfp';
  }
  function isLargeCorp() {
    return formData.business.orgType === 'largecorp';
  }
  function creditRequested() {
    return formData.credit.requested === true;
  }
  function apRequired() {
    return formData.credit.apRequired === true;
  }
  function isOverTenK() {
    return formData.credit.limit === 'Over $10,000';
  }

  // Rule 1: ABN required — shown for NOT govt
  function showABNRequired() {
    return !isGovt() && formData.business.orgType !== '';
  }
  // Rule 2: ABN optional — shown for govt
  function showABNOptional() {
    return isGovt();
  }
  // Rule 3: ACN + Trading Name — shown for NOT govt
  function showACNAndTrading() {
    return !isGovt() && formData.business.orgType !== '';
  }
  // Rule 4: Tax exemption — shown for NFP
  function showTaxExemption() {
    return isNFP();
  }
  // Rule 5: Tax exempt proof upload — shown if tax exempt = Yes
  function showTaxExemptProof() {
    return isNFP() && formData.business.taxExempt === true;
  }
  // Rule 6: PO + AP fields — shown if credit = Yes
  function showPOAndAP() {
    return creditRequested();
  }
  // Rule 7: AP step — shown if AP = Yes
  function showAPStep() {
    return creditRequested() && apRequired();
  }
  // Rule 8/9: Directors step hidden when credit not requested OR govt/largecorp/nfp
  // Rule 10: Directors step shown for private + credit
  function showDirectorsStep() {
    return isPrivate() && creditRequested();
  }
  // Rule 11/12: Personal guarantee — hidden if no credit OR govt/largecorp
  function showPersonalGuarantee() {
    return creditRequested() && !isGovt() && !isLargeCorp();
  }
  // Rule 13/13a/14: Govt credit acknowledgement — shown for govt + credit
  function showGovtCreditAck() {
    return isGovt() && creditRequested();
  }
  // Rule 15/16: Position + Organisation on terms — shown for govt only
  function showPositionOrganisation() {
    return isGovt();
  }
  // Rule 17/18: Required signed upload — only for credit + Over $10k
  function showRequiredSignedUpload() {
    return creditRequested() && isOverTenK();
  }
  // Rule 19: Credit limit dropdown — shown if credit = Yes
  function showCreditLimit() {
    return creditRequested();
  }
  // Rule 20: Optional signed upload — shown if credit + NOT Over $10k
  function showOptionalSignedUpload() {
    return creditRequested() && !isOverTenK();
  }
  // Rule 24: Govt/largecorp net terms — shown if credit + govt/largecorp
  function showGovtNetTerms() {
    return creditRequested() && (isGovt() || isLargeCorp());
  }
  // Rule 25: Business net terms — shown if credit + private/nfp
  function showBusinessNetTerms() {
    return creditRequested() && (isPrivate() || isNFP());
  }

  // Calculate total visible steps
  function getVisibleSteps() {
    const steps = [1, 2, 3]; // Always visible
    if (showAPStep()) steps.push(4);
    if (showDirectorsStep()) steps.push(5);
    steps.push(6); // Terms always visible
    return steps;
  }

  function getTotalSteps() {
    return getVisibleSteps().length;
  }

  function getStepLabel(step) {
    const labels = {
      1: 'Contact Details',
      2: 'Business Profile',
      3: 'Account Preferences',
      4: 'Accounts Payable Contact',
      5: 'Directors / Proprietors',
      6: 'Terms & Signature',
      7: 'Success',
    };
    return labels[step] || '';
  }

  function getNextStep() {
    const visible = getVisibleSteps();
    const currentIndex = visible.indexOf(currentStep);
    if (currentIndex < visible.length - 1) return visible[currentIndex + 1];
    return null; // Submit
  }

  function getPrevStep() {
    const visible = getVisibleSteps();
    const currentIndex = visible.indexOf(currentStep);
    if (currentIndex > 0) return visible[currentIndex - 1];
    return null;
  }

  function getStepNumber() {
    const visible = getVisibleSteps();
    return visible.indexOf(currentStep) + 1;
  }

  // ============================================================================
  // ABN VALIDATION (Australian Business Number)
  // ============================================================================

  function validateABN(abn) {
    // Strip spaces
    const clean = abn.replace(/\s/g, '');
    // Must be exactly 11 digits
    if (!/^\d{11}$/.test(clean)) return false;
    // Block example ABN
    if (clean === '51824753556') return false;
    // ABN checksum algorithm (ATO weighted modulus)
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const digits = clean.split('').map(Number);
    digits[0] -= 1; // Subtract 1 from first digit
    let sum = 0;
    for (let i = 0; i < 11; i++) {
      sum += digits[i] * weights[i];
    }
    return sum % 89 === 0;
  }

  // ============================================================================
  // PHONE VALIDATION (Australian format)
  // ============================================================================

  function validateAUPhone(phone) {
    // Strip spaces, dashes, parentheses
    const clean = phone.replace(/[\s\-()]/g, '');
    // Accept: 04xxxxxxxx (mobile), 0x xxxxxxxx (landline), +614xxxxxxxx, +61x xxxxxxxx
    if (/^04\d{8}$/.test(clean)) return true;
    if (/^0[2-9]\d{8}$/.test(clean)) return true;
    if (/^\+614\d{8}$/.test(clean)) return true;
    if (/^\+61[2-9]\d{8}$/.test(clean)) return true;
    if (/^614\d{8}$/.test(clean)) return true;
    if (/^61[2-9]\d{8}$/.test(clean)) return true;
    return false;
  }

  // ============================================================================
  // ACN VALIDATION (Australian Company Number)
  // ============================================================================

  function validateACN(acn) {
    // Strip spaces
    const clean = acn.replace(/\s/g, '');
    // Must be exactly 9 digits
    if (!/^\d{9}$/.test(clean)) return false;
    // ACN checksum algorithm (ASIC weighted modulus)
    const weights = [8, 7, 6, 5, 4, 3, 2, 1];
    const digits = clean.split('').map(Number);
    let sum = 0;
    for (let i = 0; i < 8; i++) {
      sum += digits[i] * weights[i];
    }
    const remainder = sum % 10;
    const checkDigit = (10 - remainder) % 10;
    return checkDigit === digits[8];
  }

  // ============================================================================
  // VALIDATION (per step)
  // ============================================================================

  function validateStep(step) {
    const errors = [];

    switch (step) {
      case 1: // Contact Details
        if (!formData.contact.firstName.trim()) errors.push('First name is required.');
        if (!formData.contact.lastName.trim()) errors.push('Last name is required.');
        if (!formData.contact.phone.trim()) errors.push('Phone number is required.');
        else if (!validateAUPhone(formData.contact.phone))
          errors.push('Please enter a valid Australian phone number (e.g. 0412 345 678 or 02 1234 5678).');
        if (!formData.contact.email.trim()) errors.push('Email is required.');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email))
          errors.push('Invalid email address.');
        if (formData.contact.email.toLowerCase() !== formData.contact.confirmEmail.toLowerCase())
          errors.push('Email addresses do not match.');
        break;

      case 2: // Business Profile
        if (!formData.business.orgType) errors.push('Organisation type is required.');
        if (!formData.business.legalName.trim()) errors.push('Legal business name is required.');
        if (showABNRequired() && !formData.business.abn.trim()) errors.push('ABN is required.');
        else if (showABNRequired() && formData.business.abn.trim() && !validateABN(formData.business.abn))
          errors.push('Please enter a valid 11-digit ABN (e.g. 51 824 753 556).');
        if (showABNOptional() && formData.business.abn.trim() && !validateABN(formData.business.abn))
          errors.push('ABN must be a valid 11-digit number if provided.');
        if (showACNAndTrading() && formData.business.acn.trim() && !validateACN(formData.business.acn))
          errors.push('Please enter a valid 9-digit ACN (e.g. 004 085 616).');
        if (!formData.business.address1.trim()) errors.push('Address is required.');
        if (!formData.business.city.trim()) errors.push('City/Town is required.');
        if (!formData.business.province) errors.push('State is required.');
        if (!formData.business.zip.trim()) errors.push('Postcode is required.');
        if (showTaxExemption() && formData.business.taxExempt === true && !formData.business.taxExemptProofUrl)
          errors.push('Proof of tax-exempt status is required.');
        break;

      case 3: // Account Preferences
        // Credit fields are optional — no required validation needed
        // But if credit requested, net terms should be selected
        if (creditRequested() && showBusinessNetTerms() && !formData.credit.netTerms)
          errors.push('Please select your preferred net terms.');
        if (creditRequested() && showGovtNetTerms() && !formData.credit.govtTerms)
          errors.push('Please select your preferred payment terms.');
        break;

      case 4: // AP Contact
        if (showAPStep()) {
          if (!formData.apContact.firstName.trim()) errors.push('AP first name is required.');
          if (!formData.apContact.lastName.trim()) errors.push('AP last name is required.');
          if (!formData.apContact.phone.trim()) errors.push('AP phone number is required.');
          else if (!validateAUPhone(formData.apContact.phone))
            errors.push('Please enter a valid Australian phone number for AP contact.');
          if (!formData.apContact.email.trim()) errors.push('AP email is required.');
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.apContact.email))
            errors.push('Invalid AP email address.');
        }
        break;

      case 5: // Directors
        if (showDirectorsStep()) {
          if (!formData.directors[0]?.fullName?.trim())
            errors.push('At least one director/proprietor name is required.');
          if (!formData.directors[0]?.positionTitle?.trim())
            errors.push('Position/title is required.');
        }
        break;

      case 6: // Terms & Signature
        if (showPersonalGuarantee() && !formData.terms.personalGuarantee)
          errors.push('You must agree to the personal guarantee.');
        if (showGovtCreditAck() && !formData.terms.govtCreditAck)
          errors.push('Government credit acknowledgement is required.');
        if (!formData.terms.wholesaleTerms) errors.push('You must agree to the wholesale terms.');
        if (!formData.terms.signatureName.trim())
          errors.push('Full legal name (signature) is required.');
        if (showPositionOrganisation() && !formData.terms.position.trim())
          errors.push('Position is required.');
        if (showPositionOrganisation() && !formData.terms.organisation.trim())
          errors.push('Organisation is required.');
        if (!formData.terms.signatureDate) errors.push('Signature date is required.');
        if (showRequiredSignedUpload() && !formData.terms.signedAgreementRequiredUrl)
          errors.push('A signed agreement upload is required for credit limits over $10,000.');
        break;
    }

    return errors;
  }

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================

  async function uploadFile(file, targetField) {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showError('Invalid file type. Accepted: PDF, JPG, PNG.');
      return null;
    }
    if (file.size > MAX_FILE_SIZE) {
      showError('File too large. Maximum 25MB.');
      return null;
    }

    try {
      // Get upload URL from worker
      const urlResponse = await fetch(`${WORKER_URL}/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentType: file.type }),
      });

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = await urlResponse.json();

      // Upload file
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      return fileUrl;
    } catch (error) {
      console.error('Upload error:', error);
      showError('File upload failed. Please try again.');
      return null;
    }
  }

  // ============================================================================
  // SUBMISSION
  // ============================================================================

  async function submitForm() {
    if (isSubmitting) return;
    isSubmitting = true;

    // Compute captured net terms
    if (showBusinessNetTerms() && formData.credit.netTerms) {
      formData.credit.capturedNetTerms = formData.credit.netTerms;
    } else if (showGovtNetTerms() && formData.credit.govtTerms) {
      formData.credit.capturedNetTerms = formData.credit.govtTerms;
    }

    const payload = {
      ...formData,
      turnstileToken,
    };

    try {
      updateSubmitButton('Submitting...', true);

      const response = await fetch(`${WORKER_URL}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      // Show success step
      currentStep = 7;
      render();
    } catch (error) {
      console.error('Submit error:', error);
      showError(error.message || 'Something went wrong. Please try again.');
      updateSubmitButton('Submit application', false);
    } finally {
      isSubmitting = false;
    }
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  function render() {
    const container = document.getElementById('b2b-registration-form');
    if (!container) return;

    if (currentStep === 7) {
      container.innerHTML = renderSuccess();
      return;
    }

    const visibleSteps = getVisibleSteps();
    const stepNum = getStepNumber();
    const total = getTotalSteps();
    const isLastStep = getNextStep() === null;

    container.innerHTML = `
      <div class="b2b-form-wrapper">
        <div class="b2b-progress">
          <div class="b2b-progress-bar">
            <div class="b2b-progress-fill" style="width: ${(stepNum / total) * 100}%"></div>
          </div>
          <p class="b2b-step-counter">Step ${stepNum} of ${total} — ${getStepLabel(currentStep)}</p>
        </div>
        
        <div class="b2b-errors" id="b2b-errors" style="display:none;"></div>

        <div class="b2b-step-content">
          ${renderStep(currentStep)}
        </div>

        <div class="b2b-nav">
          ${currentStep > 1 ? `<button type="button" class="b2b-btn b2b-btn-back" onclick="B2BForm.prev()">Back</button>` : '<div></div>'}
          ${
            isLastStep
              ? `<button type="button" class="b2b-btn b2b-btn-submit" id="b2b-submit-btn" onclick="B2BForm.submit()">Submit application</button>`
              : `<button type="button" class="b2b-btn b2b-btn-next" onclick="B2BForm.next()">Next</button>`
          }
        </div>

        <p class="b2b-cancel"><a href="/account/login">Cancel</a></p>

        ${TURNSTILE_SITE_KEY && isLastStep ? `<div class="cf-turnstile" data-sitekey="${TURNSTILE_SITE_KEY}" data-callback="B2BForm.onTurnstile"></div>` : ''}
      </div>
    `;

    bindInputs();

    // Explicitly render Turnstile widget when last step is shown
    if (isLastStep && TURNSTILE_SITE_KEY && typeof turnstile !== 'undefined') {
      const turnstileContainer = document.querySelector('.cf-turnstile');
      if (turnstileContainer && !turnstileContainer.hasChildNodes()) {
        turnstile.render(turnstileContainer, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: function(token) {
            B2BForm.onTurnstile(token);
          }
        });
      }
    }
  }

  function renderStep(step) {
    switch (step) {
      case 1:
        return renderContactDetails();
      case 2:
        return renderBusinessProfile();
      case 3:
        return renderAccountPreferences();
      case 4:
        return renderAPContact();
      case 5:
        return renderDirectors();
      case 6:
        return renderTerms();
      default:
        return '';
    }
  }

  // ============================================================================
  // STEP RENDERERS
  // ============================================================================

  function renderContactDetails() {
    return `
      <h2>Contact Details</h2>
      <p class="b2b-hint">Primary contact for this account.</p>
      
      <div class="b2b-row">
        <div class="b2b-field">
          <label for="contact-firstName">First name *</label>
          <input type="text" id="contact-firstName" data-path="contact.firstName" value="${esc(formData.contact.firstName)}" required>
        </div>
        <div class="b2b-field">
          <label for="contact-lastName">Last name *</label>
          <input type="text" id="contact-lastName" data-path="contact.lastName" value="${esc(formData.contact.lastName)}" required>
        </div>
      </div>

      <div class="b2b-field">
        <label for="contact-phone">Phone number *</label>
        <input type="tel" id="contact-phone" data-path="contact.phone" value="${esc(formData.contact.phone)}" placeholder="e.g. 0412 345 678, 02 1234 5678, or +61 04 1234 5678" required>
      </div>

      <div class="b2b-field">
        <label for="contact-email">Email address *</label>
        <input type="email" id="contact-email" data-path="contact.email" value="${esc(formData.contact.email)}" required>
        <p class="b2b-help">Used for account login and order notifications.</p>
      </div>

      <div class="b2b-field">
        <label for="contact-confirmEmail">Confirm email address *</label>
        <input type="email" id="contact-confirmEmail" data-path="contact.confirmEmail" value="${esc(formData.contact.confirmEmail)}" required>
      </div>
    `;
  }

  function renderBusinessProfile() {
    return `
      <h2>Business Profile</h2>

      <div class="b2b-field">
        <label>Organisation Type *</label>
        <p class="b2b-help">Select the option that best describes your organisation.</p>
        <div class="b2b-radios">
          ${radioOption('business.orgType', 'private', 'Private business (Company / Sole Trader / Partnership)')}
          ${radioOption('business.orgType', 'government', 'Government department / agency')}
          ${radioOption('business.orgType', 'nfp', 'Not-for-profit / Other')}
          ${radioOption('business.orgType', 'largecorp', 'Large Corporate / Multinational')}
        </div>
      </div>

      <div class="b2b-field">
        <label for="business-legalName">Legal Business Name *</label>
        <input type="text" id="business-legalName" data-path="business.legalName" value="${esc(formData.business.legalName)}" required>
      </div>

      ${
        showACNAndTrading()
          ? `
        <div class="b2b-field">
          <label for="business-tradingName">Trading Name (if different)</label>
          <input type="text" id="business-tradingName" data-path="business.tradingName" value="${esc(formData.business.tradingName)}">
        </div>
      `
          : ''
      }

      ${
        showABNRequired()
          ? `
        <div class="b2b-field">
          <label for="business-abn">ABN * ${tooltip('Your 11-digit Australian Business Number. Find yours at abr.business.gov.au')}</label>
          <input type="text" id="business-abn" data-path="business.abn" value="${esc(formData.business.abn)}" required>
        </div>
      `
          : ''
      }

      ${
        showABNOptional()
          ? `
        <div class="b2b-field">
          <label for="business-abn">ABN (optional) ${tooltip('Your 11-digit Australian Business Number, if applicable. Find yours at abr.business.gov.au')}</label>
          <input type="text" id="business-abn" data-path="business.abn" value="${esc(formData.business.abn)}">
        </div>
      `
          : ''
      }

      ${
        showACNAndTrading()
          ? `
        <div class="b2b-field">
          <label for="business-acn">ACN (if applicable) ${tooltip('9-digit Australian Company Number. Only for registered companies — not required for sole traders or partnerships.')}</label>
          <input type="text" id="business-acn" data-path="business.acn" value="${esc(formData.business.acn)}">
        </div>
      `
          : ''
      }

      <div class="b2b-field">
        <label for="business-address1">Address *</label>
        <input type="text" id="business-address1" data-path="business.address1" value="${esc(formData.business.address1)}" required>
      </div>

      <div class="b2b-field">
        <label for="business-address2">Apartment, suite, etc.</label>
        <input type="text" id="business-address2" data-path="business.address2" value="${esc(formData.business.address2)}">
      </div>

      <div class="b2b-field">
        <label for="business-city">City / Town *</label>
        <input type="text" id="business-city" data-path="business.city" value="${esc(formData.business.city)}" required>
      </div>

      <div class="b2b-row">
        <div class="b2b-field">
          <label for="business-country">Country *</label>
          <select id="business-country" data-path="business.country" disabled>
            <option value="Australia" selected>Australia</option>
          </select>
        </div>
        <div class="b2b-field">
          <label for="business-province">State *</label>
          <select id="business-province" data-path="business.province" required>
            ${AU_STATES.map((s) => `<option value="${s.value}" ${formData.business.province === s.value ? 'selected' : ''}>${s.label}</option>`).join('')}
          </select>
        </div>
      </div>

      <div class="b2b-field">
        <label for="business-zip">Postcode *</label>
        <input type="text" id="business-zip" data-path="business.zip" value="${esc(formData.business.zip)}" maxlength="4" required>
      </div>

      ${
        showTaxExemption()
          ? `
        <div class="b2b-field">
          <h3>Tax status</h3>
          <label>Is your organization tax-exempt? *</label>
          <div class="b2b-radios">
            ${radioOption('business.taxExempt', 'true', 'Yes')}
            ${radioOption('business.taxExempt', 'false', 'No')}
          </div>
        </div>

        ${
          showTaxExemptProof()
            ? `
          <div class="b2b-field">
            <label>Proof of tax-exempt status *</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onchange="B2BForm.handleFile(event, 'business.taxExemptProofUrl')">
            <p class="b2b-help">Upload proof of tax-exempt status (PDF/JPG/PNG, max 25MB).</p>
            ${formData.business.taxExemptProofUrl ? '<p class="b2b-file-ok">✓ File uploaded</p>' : ''}
          </div>
        `
            : ''
        }
      `
          : ''
      }
    `;
  }

  function renderAccountPreferences() {
    return `
      <h2>Credit Account Application</h2>

      <div class="b2b-field">
        <label>Do you want to apply for a credit account?</label>
        <p class="b2b-help">Credit terms are subject to approval. SMEs may be asked for a Personal Guarantee. Government/Large Corporates don't require guarantees (PO/partial order is fine).</p>
        <div class="b2b-radios">
          ${radioOption('credit.requested', 'true', 'Yes')}
          ${radioOption('credit.requested', 'false', 'No')}
        </div>
      </div>

      ${
        showCreditLimit()
          ? `
        <div class="b2b-field">
          <label for="credit-limit">Requested Credit Limit (AUD) ${tooltip('Choose based on your expected monthly order value. You can request an increase later.')}</label>
          <p class="b2b-help">Choose the credit limit you expect to need for typical purchases. Selecting "Over $10,000" will require a signed agreement upload.</p>
          <select id="credit-limit" data-path="credit.limit">
            <option value="">Select an option...</option>
            <option value="$2,000" ${formData.credit.limit === '$2,000' ? 'selected' : ''}>$2,000</option>
            <option value="$5,000" ${formData.credit.limit === '$5,000' ? 'selected' : ''}>$5,000</option>
            <option value="$10,000" ${formData.credit.limit === '$10,000' ? 'selected' : ''}>$10,000</option>
            <option value="Over $10,000" ${formData.credit.limit === 'Over $10,000' ? 'selected' : ''}>Over $10,000</option>
          </select>
        </div>
      `
          : ''
      }

      ${
        showBusinessNetTerms()
          ? `
        <div class="b2b-field">
          <label for="credit-netTerms">Net Terms ${tooltip('How many days after invoice date until payment is due. Net 14 = 14 days, Net 30 = 30 days.')}</label>
          <p class="b2b-help">Select your preferred invoice due date for approved credit (e.g., Net 14 or Net 30). This guides invoicing terms on your account.</p>
          <select id="credit-netTerms" data-path="credit.netTerms">
            <option value="">Select an option...</option>
            <option value="Net 14" ${formData.credit.netTerms === 'Net 14' ? 'selected' : ''}>Net 14</option>
            <option value="Net 30" ${formData.credit.netTerms === 'Net 30' ? 'selected' : ''}>Net 30</option>
          </select>
        </div>
      `
          : ''
      }

      ${
        showOptionalSignedUpload()
          ? `
        <div class="b2b-field">
          <label>Upload signed agreement (optional)</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onchange="B2BForm.handleFile(event, 'credit.signedAgreementUrl')">
          <p class="b2b-help">Optional: upload a scanned signed agreement if your organisation requires a wet signature. Not required for limits under $10,000. Accepted: PDF/JPG/PNG (max 25 MB). <a href="${WHOLESALE_AGREEMENT_URL}" target="_blank">Download Wholesale Agreement</a></p>
          ${formData.credit.signedAgreementUrl ? '<p class="b2b-file-ok">✓ File uploaded</p>' : ''}
        </div>
      `
          : ''
      }

      ${
        showGovtNetTerms()
          ? `
        <div class="b2b-field">
          <label for="credit-govtTerms">Preferred Payment Terms (GOVT)</label>
          <p class="b2b-help">Government applicants only. Select the standard payment terms (Net 30/45/60) used by your department for approved credit.</p>
          <select id="credit-govtTerms" data-path="credit.govtTerms">
            <option value="">Select an option...</option>
            <option value="Net 30" ${formData.credit.govtTerms === 'Net 30' ? 'selected' : ''}>Net 30</option>
            <option value="Net 45" ${formData.credit.govtTerms === 'Net 45' ? 'selected' : ''}>Net 45</option>
            <option value="Net 60" ${formData.credit.govtTerms === 'Net 60' ? 'selected' : ''}>Net 60</option>
          </select>
        </div>
      `
          : ''
      }

      ${
        showPOAndAP()
          ? `
        <div class="b2b-field">
          <label>Do you require a Purchase Order (PO) before we supply/invoice?</label>
          <div class="b2b-radios">
            ${radioOption('credit.poRequired', 'Yes - POs are mandatory', 'Yes - POs are mandatory')}
            ${radioOption('credit.poRequired', 'No — POs not required', 'No — POs not required')}
            ${radioOption('credit.poRequired', 'Sometimes — depends on order/value/department', 'Sometimes — depends on order/value/department')}
          </div>
        </div>

        <div class="b2b-field">
          <label>Do Invoices need to be directed to your AP accounting team? *</label>
          <div class="b2b-radios">
            ${radioOption('credit.apRequired', 'true', 'Yes')}
            ${radioOption('credit.apRequired', 'false', 'No')}
          </div>
        </div>
      `
          : ''
      }
    `;
  }

  function renderAPContact() {
    return `
      <h2>Accounts Payable Contact</h2>

      <div class="b2b-row">
        <div class="b2b-field">
          <label for="ap-firstName">First Name *</label>
          <input type="text" id="ap-firstName" data-path="apContact.firstName" value="${esc(formData.apContact.firstName)}" placeholder="AP First Name" required>
        </div>
        <div class="b2b-field">
          <label for="ap-lastName">Last Name *</label>
          <input type="text" id="ap-lastName" data-path="apContact.lastName" value="${esc(formData.apContact.lastName)}" placeholder="AP Last Name" required>
        </div>
      </div>

      <div class="b2b-field">
        <label for="ap-phone">Accounts Phone Number *</label>
        <input type="tel" id="ap-phone" data-path="apContact.phone" value="${esc(formData.apContact.phone)}" placeholder="e.g. 0412 345 678, 02 1234 5678, or +61 04 1234 5678" required>
      </div>

      <div class="b2b-field">
        <label for="ap-email">Accounts Email *</label>
        <input type="email" id="ap-email" data-path="apContact.email" value="${esc(formData.apContact.email)}" placeholder="Accounts Email Address" required>
      </div>
    `;
  }

  function renderDirectors() {
    return `
      <h2>Director / Proprietor</h2>
      <p class="b2b-hint">Details of each Director or Proprietor for credit guarantee.</p>

      <div id="directors-list">
        ${formData.directors
          .map(
            (d, i) => `
          <div class="b2b-director-card">
            <div class="b2b-field">
              <label>Full Name *</label>
              <input type="text" data-director="${i}" data-key="fullName" value="${esc(d.fullName)}" required>
            </div>
            <div class="b2b-field">
              <label>Position/Title *</label>
              <input type="text" data-director="${i}" data-key="positionTitle" value="${esc(d.positionTitle)}" required>
            </div>
            ${i > 0 ? `<button type="button" class="b2b-btn-remove" onclick="B2BForm.removeDirector(${i})">Remove</button>` : ''}
          </div>
        `
          )
          .join('')}
      </div>

      <button type="button" class="b2b-btn b2b-btn-add" onclick="B2BForm.addDirector()">+ Add Person</button>
    `;
  }

  function renderTerms() {
    return `
      <h2>Terms & Signature</h2>

      ${
        showPersonalGuarantee()
          ? `
        <div class="b2b-section">
          <h3>Personal Guarantee ${tooltip('Directors may be personally liable for outstanding invoices if the business defaults on payment.')}</h3>
          <div class="b2b-field">
            <label class="b2b-checkbox">
              <input type="checkbox" data-path="terms.personalGuarantee" ${formData.terms.personalGuarantee ? 'checked' : ''}>
              <span>I agree to the Personal Guarantee *</span>
            </label>
            <p class="b2b-help">By checking this box you agree to the personal guarantee as set out in the Wholesale Terms & Conditions. <a href="${WHOLESALE_AGREEMENT_URL}" target="_blank">Wholesale Agreement</a></p>
          </div>
        </div>
      `
          : ''
      }

      ${
        showGovtCreditAck()
          ? `
        <div class="b2b-section">
          <h3>Government Credit Acknowledgement</h3>
          <div class="b2b-field">
            <label class="b2b-checkbox">
              <input type="checkbox" data-path="terms.govtCreditAck" ${formData.terms.govtCreditAck ? 'checked' : ''}>
              <span>Credit Acknowledgement *</span>
            </label>
            <p class="b2b-help">By checking this box, I confirm I am authorised to request credit on behalf of my department/agency and bind the department or agency to this agreement.</p>
          </div>
        </div>
      `
          : ''
      }

      <div class="b2b-section">
        <h3>Terms agreement</h3>
        <div class="b2b-field">
          <label class="b2b-checkbox">
            <input type="checkbox" data-path="terms.wholesaleTerms" ${formData.terms.wholesaleTerms ? 'checked' : ''}>
            <span>I agree to Wholesale Terms *</span>
          </label>
          <p class="b2b-help">By checking this box you agree to the following terms and conditions: <a href="${WHOLESALE_AGREEMENT_URL}" target="_blank">Wholesale Agreement</a></p>
        </div>
      </div>

      <div class="b2b-field">
        <label for="terms-signatureName">Type full legal name as signature *</label>
        <input type="text" id="terms-signatureName" data-path="terms.signatureName" value="${esc(formData.terms.signatureName)}" required>
        <p class="b2b-help">By submitting this online application, I acknowledge and agree that typing my full legal name and date below constitutes a valid electronic signature under the Electronic Transactions Act 1999 (Cth) and binds the applicant organisation to the Wholesale Trading Agreement.</p>
      </div>

      ${
        showPositionOrganisation()
          ? `
        <div class="b2b-field">
          <label for="terms-position">Position *</label>
          <input type="text" id="terms-position" data-path="terms.position" value="${esc(formData.terms.position)}" placeholder="e.g. Procurement Manager" required>
          <p class="b2b-help">Position/Title of the authorised officer who can bind the department/agency to the credit terms (e.g., Procurement Manager, Finance Officer).</p>
        </div>

        <div class="b2b-field">
          <label for="terms-organisation">Organisation *</label>
          <input type="text" id="terms-organisation" data-path="terms.organisation" value="${esc(formData.terms.organisation)}" placeholder="e.g. NSW Department of Transport – Procurement" required>
          <p class="b2b-help">Full legal name of the department/agency requesting credit (e.g., Department of XYZ — ABC Division).</p>
        </div>
      `
          : ''
      }

      <div class="b2b-field">
        <label for="terms-signatureDate">Signature Date *</label>
        <input type="date" id="terms-signatureDate" data-path="terms.signatureDate" value="${formData.terms.signatureDate}" required>
      </div>

      ${
        showRequiredSignedUpload()
          ? `
        <div class="b2b-field">
          <label>Upload signed agreement (required for Over $10k) *</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onchange="B2BForm.handleFile(event, 'terms.signedAgreementRequiredUrl')">
          <p class="b2b-help">Required when "Requested Credit Limit (AUD)" is set to "Over $10,000". Upload a signed agreement (PDF/JPG/PNG, max 25 MB). <a href="${WHOLESALE_AGREEMENT_URL}" target="_blank">Download Wholesale Agreement</a></p>
          ${formData.terms.signedAgreementRequiredUrl ? '<p class="b2b-file-ok">✓ File uploaded</p>' : ''}
        </div>
      `
          : ''
      }
    `;
  }

  function renderSuccess() {
    return `
      <div class="b2b-success">
        <h2>Success!</h2>
        <p>Your submission has been received.</p>
        <p>We'll review your application within 1–2 business days and be in touch via email.</p>
        <a href="/" class="b2b-btn b2b-btn-next">Back to home</a>
      </div>
    `;
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  function radioOption(path, value, label) {
    const currentVal = getNestedValue(formData, path);
    // Handle boolean string comparison
    let isChecked = false;
    if (typeof currentVal === 'boolean') {
      isChecked = currentVal.toString() === value;
    } else {
      isChecked = currentVal === value;
    }

    const id = `${path}-${value}`.replace(/[.\s]/g, '-');
    return `
      <label class="b2b-radio" for="${id}">
        <input type="radio" id="${id}" name="${path}" value="${esc(value)}" ${isChecked ? 'checked' : ''} onchange="B2BForm.setRadio('${path}', '${esc(value)}')">
        <span>${label}</span>
      </label>
    `;
  }

  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function tooltip(text) {
    return `<span class="b2b-tooltip"><span class="b2b-tooltip-icon">i</span><span class="b2b-tooltip-text">${text}</span></span>`;
  }

  function getNestedValue(obj, path) {
    return path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
  }

  function setNestedValue(obj, path, value) {
    const keys = path.split('.');
    const last = keys.pop();
    const target = keys.reduce((o, k) => o[k], obj);
    target[last] = value;
  }

  function bindInputs() {
    // Bind text/email/tel/date inputs
    document.querySelectorAll('#b2b-registration-form input[data-path], #b2b-registration-form select[data-path]').forEach((el) => {
      el.addEventListener('input', (e) => {
        setNestedValue(formData, e.target.dataset.path, e.target.value);
      });
      el.addEventListener('change', (e) => {
        setNestedValue(formData, e.target.dataset.path, e.target.value);
        // Re-render if the field affects conditional logic
        const reRenderPaths = ['business.orgType', 'credit.requested', 'credit.limit', 'credit.apRequired', 'business.taxExempt'];
        if (reRenderPaths.includes(e.target.dataset.path)) {
          render();
        }
      });
    });

    // Bind checkbox inputs
    document.querySelectorAll('#b2b-registration-form input[type="checkbox"][data-path]').forEach((el) => {
      el.addEventListener('change', (e) => {
        setNestedValue(formData, e.target.dataset.path, e.target.checked);
      });
    });

    // Bind director inputs
    document.querySelectorAll('#b2b-registration-form input[data-director]').forEach((el) => {
      el.addEventListener('input', (e) => {
        const idx = parseInt(e.target.dataset.director);
        const key = e.target.dataset.key;
        formData.directors[idx][key] = e.target.value;
      });
    });
  }

  function showError(message) {
    const el = document.getElementById('b2b-errors');
    if (el) {
      el.style.display = 'block';
      el.innerHTML = `<p>${message}</p>`;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function showErrors(messages) {
    const el = document.getElementById('b2b-errors');
    if (el) {
      el.style.display = 'block';
      el.innerHTML = messages.map((m) => `<p>${m}</p>`).join('');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function hideErrors() {
    const el = document.getElementById('b2b-errors');
    if (el) el.style.display = 'none';
  }

  function updateSubmitButton(text, disabled) {
    const btn = document.getElementById('b2b-submit-btn');
    if (btn) {
      btn.textContent = text;
      btn.disabled = disabled;
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  window.B2BForm = {
    init() {
      // Set signature date to today (user's local date)
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      formData.terms.signatureDate = `${yyyy}-${mm}-${dd}`;
      render();
    },

    next() {
      hideErrors();
      const errors = validateStep(currentStep);
      if (errors.length > 0) {
        showErrors(errors);
        return;
      }
      const next = getNextStep();
      if (next) {
        currentStep = next;
        render();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        submitForm();
      }
    },

    prev() {
      hideErrors();
      const prev = getPrevStep();
      if (prev) {
        currentStep = prev;
        render();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    submit() {
      hideErrors();
      const errors = validateStep(currentStep);
      if (errors.length > 0) {
        showErrors(errors);
        return;
      }
      submitForm();
    },

    setRadio(path, value) {
      // Convert string booleans
      if (value === 'true') value = true;
      else if (value === 'false') value = false;

      setNestedValue(formData, path, value);

      // Re-render for conditional fields
      render();
    },

    async handleFile(event, targetPath) {
      const file = event.target.files[0];
      if (!file) return;

      const uploadingText = event.target.parentElement.querySelector('.b2b-help');
      if (uploadingText) uploadingText.textContent = 'Uploading...';

      const url = await uploadFile(file, targetPath);
      if (url) {
        setNestedValue(formData, targetPath, url);
        render();
      } else if (uploadingText) {
        uploadingText.textContent = 'Upload failed. Try again.';
      }
    },

    addDirector() {
      formData.directors.push({ fullName: '', positionTitle: '' });
      render();
    },

    removeDirector(index) {
      formData.directors.splice(index, 1);
      render();
    },

    onTurnstile(token) {
      turnstileToken = token;
    },
  };

  // Auto-init when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => B2BForm.init());
  } else {
    B2BForm.init();
  }
})();
